/**
 * Trace view (route `/traces/:traceId`, Req 11.3). Loads a trace index entry
 * (`GET /v1/traces/{traceId}`) and renders its metadata — the trace id, the originating result and
 * check (linked), and the S3 object key where the OpenTelemetry spans are stored.
 *
 * The spans themselves live in S3; rendering a full span waterfall (fetching + parsing the spans
 * object) is a later enhancement. This pass shows the `spansS3Key` as a labelled reference.
 */

import { Link, useParams } from 'react-router-dom';

import { useApiClient } from '../api/use-api-client';
import type { GetTraceResponse, TraceDto } from '../api/types';
import { ErrorState, LoadingState } from '../components/LoadStates';
import { useResource } from '../lib/use-resource';

export function TraceDetailPage(): JSX.Element {
  const client = useApiClient();
  const { traceId } = useParams();
  const id = traceId ?? '';

  const trace = useResource<GetTraceResponse>(() => client.getTrace(id), [client, id]);

  if (trace.status === 'loading') {
    return (
      <section>
        <h1>Trace</h1>
        <LoadingState label="Loading trace…" />
      </section>
    );
  }

  if (trace.status === 'error') {
    return (
      <section>
        <h1>Trace</h1>
        <ErrorState summary="Could not load this trace." message={trace.message} isAuthError={trace.isAuthError} />
      </section>
    );
  }

  return <TraceBody trace={trace.data.trace} />;
}

function TraceBody({ trace }: { trace: TraceDto }): JSX.Element {
  return (
    <section>
      <p className="muted">
        <Link to={`/results/${encodeURIComponent(trace.resultId)}`}>← Back to result</Link>
      </p>
      <h1>Trace</h1>

      <dl className="detail-list">
        <div className="detail-row">
          <dt>Trace ID</dt>
          <dd>
            <code>{trace.traceId}</code>
          </dd>
        </div>
        <div className="detail-row">
          <dt>Result</dt>
          <dd>
            <Link to={`/results/${encodeURIComponent(trace.resultId)}`}>{trace.resultId}</Link>
          </dd>
        </div>
        <div className="detail-row">
          <dt>Check</dt>
          <dd>
            <Link to={`/checks/${encodeURIComponent(trace.checkId)}`}>{trace.checkId}</Link>
          </dd>
        </div>
        <div className="detail-row">
          <dt>Spans object</dt>
          <dd>
            <code>{trace.spansS3Key}</code>
          </dd>
        </div>
      </dl>

      <p className="muted">A full span waterfall (fetched and parsed from the spans object) is a later enhancement.</p>
    </section>
  );
}
